/* Where a DOH sits against its corridor.
 *
 * A METER, not a chart: one value against one band. The band is drawn
 * because the band IS the decision rule - a bare number tells a planner
 * nothing until they remember that this family targets 18-19 and the one
 * below it targets 8-10.
 *
 * The scale runs from 0 to twice the maximum, clamped. A fixed scale would
 * make a family at 82 days unreadable; a scale fitted to the value would
 * make every family look the same, which is precisely what the planner is
 * trying to tell apart.
 */

import type { Status } from '../api/client'

export function DohMeter({
  value,
  min,
  max,
  width = 112,
}: {
  value: number
  min: number
  max: number
  width?: number
}) {
  // Headroom above the corridor, so a value inside the band does not sit on
  // the far-left edge of the track.
  const scale = Math.max(max * 2, value * 1.1, 1)
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / scale) * 100))}%`

  const status: Status = value < min ? 'LOW' : value > max ? 'HIGH' : 'OK'
  const tone = status === 'LOW' ? 'critical' : status === 'HIGH' ? 'warning' : 'good'

  return (
    <div
      className="doh-meter"
      style={{ width }}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={scale}
      aria-label={`${value.toFixed(1)} days on hand, target ${min} to ${max}`}
      title={`${value.toFixed(1)} days · corridor ${min}–${max}`}
    >
      <span
        className="band"
        style={{ left: pct(min), width: `calc(${pct(max)} - ${pct(min)})` }}
      />
      <span className={`needle ${tone}`} style={{ left: pct(value) }} />
    </div>
  )
}
